// Chat attachment storage helpers — buyer/seller chat (Conversation.js).
// Uploads to the private `chat-attachments` bucket using the same folder[1]=uid
// owner-write convention as card-images (see migration 20260605091201). Reads
// go through short-lived signed URLs; storage RLS limits them to the uploader
// and the conversation's participants (migrations 20261005090000/090100).
// Images are downsized via the shared resizeImage; other files upload as-is
// with their MIME type.

import { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from './supabaseClient';
import { resizeImage } from './CardImage';

const BUCKET = 'chat-attachments';
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024; // 15 MB — matches bucket cap

export function isImageType(type) {
  return typeof type === 'string' && type.startsWith('image/');
}

export function formatBytes(n) {
  if (n == null || !Number.isFinite(Number(n))) return '';
  const bytes = Number(n);
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Signed URLs — the bucket is private; storage RLS lets only the uploader and
// the two conversation participants read an object. URLs are cached per path
// and re-signed a few minutes before they expire.
const SIGNED_URL_TTL = 3600; // seconds
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
const signedCache = new Map(); // path -> { url, expiresAt } | Promise

export async function signAttachmentUrl(path, { force = false } = {}) {
  if (!path) return null;
  const hit = signedCache.get(path);
  if (!force && hit) {
    if (hit instanceof Promise) return hit;
    if (hit.expiresAt - Date.now() > REFRESH_MARGIN_MS) return hit.url;
  }
  const p = supabase.storage.from(BUCKET).createSignedUrl(path, SIGNED_URL_TTL)
    .then(({ data, error }) => {
      if (error || !data?.signedUrl) { signedCache.delete(path); return null; }
      signedCache.set(path, { url: data.signedUrl, expiresAt: Date.now() + SIGNED_URL_TTL * 1000 });
      return data.signedUrl;
    })
    .catch(() => { signedCache.delete(path); return null; });
  signedCache.set(path, p);
  return p;
}

// React hook: { url, status: 'loading' | 'ready' | 'error', retry }.
// Schedules a re-sign before expiry so long-open chats keep working links;
// `retry` forces a fresh signature (used by <img onError>).
export function useAttachmentUrl(path) {
  const [state, setState] = useState({ url: null, status: path ? 'loading' : 'error' });
  const [nonce, setNonce] = useState(0);
  const forceRef = useRef(false);

  useEffect(() => {
    if (!path) { setState({ url: null, status: 'error' }); return undefined; }
    let cancelled = false;
    let timer;
    const force = forceRef.current;
    forceRef.current = false;
    signAttachmentUrl(path, { force }).then(url => {
      if (cancelled) return;
      setState(url ? { url, status: 'ready' } : { url: null, status: 'error' });
      if (url) {
        timer = setTimeout(() => setNonce(n => n + 1),
          SIGNED_URL_TTL * 1000 - REFRESH_MARGIN_MS);
      }
    });
    return () => { cancelled = true; clearTimeout(timer); };
  }, [path, nonce]);

  const retry = useCallback(() => {
    forceRef.current = true;
    setState(s => ({ ...s, status: 'loading' }));
    setNonce(n => n + 1);
  }, []);

  return { ...state, retry };
}

function extFromName(name, fallback) {
  const dot = name ? name.lastIndexOf('.') : -1;
  if (dot > 0 && dot < name.length - 1) return name.slice(dot).toLowerCase();
  return fallback;
}

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
}

// Upload one file and return the columns to persist on the message row.
// Throws on failure so the caller can surface an error to the user.
export async function uploadChatAttachment(userId, conversationId, file) {
  const image = isImageType(file.type);
  let blob = file;
  let contentType = file.type || 'application/octet-stream';
  let ext = extFromName(file.name, image ? '.jpg' : '');

  if (image) {
    // Downsize for chat; keeps screenshots legible while shrinking payload.
    blob = await resizeImage(file, 1600, 1600);
    contentType = 'image/jpeg';
    ext = '.jpg';
  }

  const path = `${userId}/${conversationId}/${randomId()}${ext}`;
  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, blob, { upsert: false, contentType });
  if (error) throw error;

  return {
    attachment_path: path,
    attachment_type: contentType,
    attachment_name: file.name || (image ? 'image.jpg' : 'file'),
    attachment_size: blob.size,
  };
}
